/**
 * Load JobTread job-targeted custom fields for tracking-category source dropdowns.
 * @returns {Promise<Array<{ id: string, name: string, type?: string }>>}
 */
export async function loadJobCustomFields() {
  const fetchOnce = () =>
    fetch('/api/jt/custom-fields', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

  let res;
  try {
    res = await fetchOnce();
  } catch (networkErr) {
    // Brief retry — common right after a local server restart
    await new Promise((r) => setTimeout(r, 400));
    try {
      res = await fetchOnce();
    } catch (retryErr) {
      throw new Error(
        'Could not reach the server. If you just restarted it, wait a moment and refresh the page.'
      );
    }
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.details || data.error || `Failed to load custom fields (${res.status})`);
  }

  const data = await res.json();
  return Array.isArray(data.customFields) ? data.customFields : [];
}

export function customFieldOptionValue(id) {
  return `cf:${id}`;
}

export function isCustomFieldOption(value) {
  return typeof value === 'string' && value.startsWith('cf:');
}

export function customFieldIdFromOption(value) {
  if (!isCustomFieldOption(value)) return null;
  return value.slice(3);
}

/**
 * If the saved selection is a custom field no longer in the live list, return a
 * stale option descriptor so the dropdown can still show the current value.
 */
export function staleCustomFieldOption(selectedValue, customFields) {
  if (!isCustomFieldOption(selectedValue)) return null;
  const id = customFieldIdFromOption(selectedValue);
  if (customFields.some((f) => f.id === id)) return null;
  return { id, name: 'Unknown custom field (removed from JobTread)' };
}
